import type { TableColumnType } from 'antd';
import type { ProdutoBodyPacoteBarra } from '@/features/produto/types/ProdutoBody';
import type { ProdutoUnidade } from '@/features/produto/types/ProdutoUnidade';
import { BadgeOnOff } from '@/components/revisar/badge/BadgeOnOff';
import { LabelWithDescription } from '@/components/revisar/LabelWithDescription';
import { getButtonsEditDelete } from '@/components/table/table-actions/TableActionsEditDelete';
import { TableActionsInline } from '@/components/table/table-actions/TableActionsInline';
import { calcularPrecoVendaProdutoPacote } from '@/features/produto/util/preco';
import { numberToCurrency } from '@/std/format';

export function produtoTabEstoquePacoteTableColumns<
    T extends ProdutoBodyPacoteBarra = ProdutoBodyPacoteBarra,
>({
    exibirCheckboxCatalogo,
    unidadeProduto,
    precoVendaProduto,
    unidades,
    onEdit,
    onDelete,
    disabled = false,
}: {
    exibirCheckboxCatalogo: boolean;
    unidadeProduto: string;
    precoVendaProduto: number;
    unidades: ProdutoUnidade[];
    onEdit: (row: T) => void;
    onDelete: (row: T) => void;
    disabled?: boolean;
}): TableColumnType<T>[] {
    return [
        ...(exibirCheckboxCatalogo
            ? [
                  {
                      align: 'center',
                      dataIndex: 'pcb_exibir_catalogo',
                      render: (v) => (
                          <BadgeOnOff condition={v === 'S'} offText='Não' onText='Sim' />
                      ),
                      title: (
                          <LabelWithDescription
                              description='Ao ativar este recurso, esta variação de código/pacote barras será disponibilizada para os clientes comprarem diretamente pelo catálogo digital.'
                              label='Catálogo'
                          />
                      ),
                  } as TableColumnType<T>,
              ]
            : []),
        {
            align: 'center',
            dataIndex: 'pcb_multiplicador',
            render: (v) => `${v || 0} ${unidadeProduto}`,
            title: (
                <LabelWithDescription
                    description='Informe a quantidade que será baixada do estoque deste produto para cada unidade vendida deste pacote/barras.'
                    label='Qtde a baixar do estoque'
                />
            ),
        },
        {
            align: 'center',
            dataIndex: 'pcb_produto_unidade_idpk',
            render: (v) => unidades.find((u) => u.pru_idpk === v)?.pru_descricao || '',
            title: 'Unidade desejada',
        },
        {
            align: 'center',
            dataIndex: 'pcb_cod_barra',
            title: 'Código de barras',
        },
        {
            dataIndex: 'pcb_descricao',
            title: 'Descrição',
        },
        {
            align: 'right',
            key: 'preco_venda',
            render: (_, row) => {
                const { preco, description } = calcularPrecoVendaProdutoPacote({
                    pcb_multiplicador: row.pcb_multiplicador ?? 0,
                    pcb_tipo_preco: row.pcb_tipo_preco,
                    pcb_valor: row.pcb_valor ?? 0,
                    pro_preco_venda: precoVendaProduto,
                });

                return (
                    <div>
                        <p>{numberToCurrency(preco)}</p>
                        <p className='text-sm text-gray-500'>{description}</p>
                    </div>
                );
            },
            title: 'Preço de venda',
        },
        {
            align: 'center',
            key: 'acoes',
            render: (_, row) => {
                const buttons = getButtonsEditDelete({
                    disabled,
                    onDelete: () => onDelete(row),
                    onEdit: () => onEdit(row),
                });

                return <TableActionsInline buttons={buttons} />;
            },
            title: 'Ações',
        },
    ];
}
